import { useMemo, useState } from 'react';
import { useSelector } from '@ecommerce/nest-ssr/common';
import { FiltersGroup } from 'modules/shared/components/filters/interfaces/filters-group.interface';
import { ProducerStateInterface } from 'modules/producers/interfaces/producer-state.interface';

export const useProducerFilters = (): { groups: FiltersGroup[]; } => {
    const ids = useSelector((state: ProducerStateInterface) => state.producerUrlManager.filters);
    const filters = useSelector((state: ProducerStateInterface) => state.category?.filters || { groups: [] });
    const aggregations = useSelector((state: ProducerStateInterface) => state.aggregations);

    // We save initial filters state
    const [initialIds] = useState(ids ?? []);

    // First we create memomized counters map. It not changed after page is loaded
    // so we don't need to recalculate something is changed.
    const counters = useMemo(() => {
        const maps = new Map<string, number>();

        aggregations.filters.forEach(item => {
            maps.set(item.filterId, item.count);
        });

        return maps;
    }, [aggregations]);

    // Next we create memomized filters groups with entries. It is the base for further operations.
    // In this step we also sort items to set selected items at the top of list.
    const groups = useMemo(() => {
        const result: FiltersGroup[] = [];

        filters.groups.forEach(group => {
            const item: FiltersGroup = { ...group, entries: [], selected: 0 };

            group.entries.forEach(entry => {
                if (counters.get(entry.id) || initialIds.includes(entry.id)) {
                    if (initialIds.includes(entry.id)) {
                        item.selected++;
                        item.entries.push({ ...entry, checked: true, count: counters.get(entry.id) ?? 0 });
                    } else {
                        item.entries.push({ ...entry, checked: false, count: counters.get(entry.id) ?? 0 });
                    }
                }
            });

            item.entries.sort((a, b) => {
                if (a.checked && b.checked) {
                    return 0;
                }

                if (a.checked) {
                    return -1;
                }

                if (b.checked) {
                    return 1;
                }

                return a.name.localeCompare(b.name);
            });

            result.push(item);
        });

        return result;
    }, [initialIds, filters, counters]);

    // The last section is recalculated for every filters changes
    return useMemo(() => {
        const result: FiltersGroup[] = [];

        groups.forEach(group => {
            const item: FiltersGroup = { ...group, entries: [], selected: 0 };

            group.entries.forEach(entry => {
                if (ids?.includes(entry.id)) {
                    item.selected++;
                    item.entries.push({ ...entry, checked: true });
                } else {
                    item.entries.push({ ...entry, checked: false });
                }
            });

            if (item.entries.length) {
                result.push(item);
            }
        });

        return { groups: result };
    }, [ids, groups]);
};
